import React, { useState, useEffect } from 'react';
import {
  Box, Button, TextField, Autocomplete,
  Typography, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import { B2BClientService } from '../../../services/B2BServices';

const PriceExceptionsManager = ({ exceptions = [], onExceptionsChange }) => {
  const { t } = useTranslation();
  
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [productsLoaded, setProductsLoaded] = useState(false);

  useEffect(() => {
    if (!exceptions.length) {
      return;
    }

    // Si el cliente ya tiene excepciones guardadas, preparamos la lista cuando
    // el usuario vaya a interactuar con el selector, pero sin bloquear el modal.
    setProductsLoaded(false);
  }, [exceptions.length]);

  const loadProducts = async () => {
    if (loading || productsLoaded) {
      return;
    }

    try {
      setLoading(true);
      const productsData = await B2BClientService.getAvailableProducts();
      setProducts(Array.isArray(productsData?.products) ? productsData.products : []);
      setProductsLoaded(true);
    } catch (error) {
      console.error('Error fetching products for price exceptions:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddException = () => {
    if (selectedProduct) {
      const exception = {
        productId: selectedProduct.id,
        productName: selectedProduct.name,
        specialPrice: ''
      };
      
      onExceptionsChange([...exceptions, exception]);
      setSelectedProduct(null);
    }
  };

  const handleRemoveException = (index) => {
    const updatedExceptions = exceptions.filter((_, i) => i !== index);
    onExceptionsChange(updatedExceptions);
  };

  const handleExceptionChange = (index, field, value) => {
    const updatedExceptions = exceptions.map((exception, i) => 
      i === index ? { ...exception, [field]: value } : exception
    );
    onExceptionsChange(updatedExceptions);
  };

  return (
    <Box>
      {/* Formulario para agregar excepción */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', flexWrap: 'wrap' }}>
          <Autocomplete
            sx={{ minWidth: 300 }}
            options={products.filter(product => !exceptions.some(ex => ex.productId === product.id))}
            getOptionLabel={(option) => option.name}
            value={selectedProduct}
            onOpen={loadProducts}
            onChange={(event, newValue) => {
              setSelectedProduct(newValue);
            }}
            loading={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('product')}
                placeholder={t('search_product')}
                helperText={!productsLoaded ? t('search_product') : ''}
                onFocus={loadProducts}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography variant="body1">
                    {option.name}
                  </Typography>
                  {option.category && (
                    <Typography variant="caption" color="text.secondary">
                      Categoría: {option.category.name}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          />

          <Button
            variant="outlined"
            startIcon={getIcon('add')}
            onClick={handleAddException}
            disabled={!selectedProduct}
            sx={{ alignSelf: 'stretch', minHeight: 56 }}
          >
            {t('add')}
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {t('add_exception_help')}
        </Typography>
      </Paper>

      {/* Tabla de excepciones */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('product')}</TableCell>
              <TableCell align="center">{t('final_price')}</TableCell>
              <TableCell align="center">{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {exceptions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    {t('no_price_exceptions')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              exceptions.map((exception, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Typography variant="body1" fontWeight="bold">
                      {exception.productName}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      value={exception.specialPrice || ''}
                      onChange={e => handleExceptionChange(index, 'specialPrice', e.target.value)}
                      size="small"
                      sx={{ width: 120 }}
                      placeholder="0.00"
                      InputProps={{ startAdornment: '$' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton 
                      onClick={() => handleRemoveException(index)}
                      color="error"
                      size="small"
                    >
                      {getIcon('trash')}
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PriceExceptionsManager;
